import { BEIGE_300, BEIGE_600, CORAL_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { KeyboardEvent, useCallback, useMemo, useState } from 'react'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { KeyboardEventKey } from 'constants/misc'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUITooltip } from 'components/common/MUITooltip'
import { RealEstatePropertyListItemDTO } from 'models/property'
import propertyPlaceholder from 'assets/img/property/property-placeholder.webp'
import { usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'
import { useTranslation } from 'react-i18next'

/**
 * @component SearchProperty component in Address step
 * @example <SearchProperty />
 */
export const SearchProperty: React.FC = () => {
  const { t } = useTranslation('order', { keyPrefix: 'step_address' })

  const {
    searchProperty,
    setSearchProperty,
    selectedProperty,
    setSelectedProperty,
    setSelectedPropertyType,
    searchPropertyData,
    resetRealEstateProperty,
    getSearchProperty,
    searchPropertyContent,
    isUpdatePropertyAddressFlow,
  } = usePurchaseFlowRealEstateProperty()

  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const selectedPropertyAddress = useMemo(() => {
    if (!selectedProperty) return {}
    if (!selectedProperty.address) return {}

    return {
      street: selectedProperty?.address.street,
      streetNumber: selectedProperty?.address.streetNumber,
      city: selectedProperty?.address.city,
      postalCode: selectedProperty?.address.postalCode,
      country: selectedProperty?.address.country,
    }
  }, [selectedProperty])

  const handleSelect = useCallback((property: RealEstatePropertyListItemDTO) => {
    const { street, streetNumber, city, postalCode, country } = property.address || {}
    const propertyAddress = [
      `${street} ${streetNumber}`,
      city,
      postalCode,
      country,
    ].filter(Boolean).join(', ')

    setSearchProperty(propertyAddress)
    setSelectedProperty(property)
    setHighlightedIndex(0)
    setSelectedPropertyType(property.propertyUsage)
  }, [setSearchProperty, setSelectedProperty, setSelectedPropertyType])

  const handleInputKeyDown = useCallback((e: KeyboardEvent, onCloseDropdown: () => void) => {
    if (!searchPropertyContent) return

    switch (e.key) {
      case KeyboardEventKey.ARROW_DOWN:
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % searchPropertyContent.length)
        break

      case KeyboardEventKey.ARROW_UP:
        e.preventDefault()
        setHighlightedIndex((prev) => (prev - 1 + searchPropertyContent.length) % searchPropertyContent.length)
        break

      case KeyboardEventKey.ESCAPE:
        e.preventDefault()
        break

      case KeyboardEventKey.ENTER:
        if (searchPropertyContent[highlightedIndex]) {
          handleSelect(searchPropertyContent[highlightedIndex])
          onCloseDropdown()
        }
        break

      default:
        break
    }
  }, [handleSelect, highlightedIndex, searchPropertyContent])

  return (
    <>
      {!isUpdatePropertyAddressFlow &&
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(isOpen, action, onClose) => (
            <MUIDropdownInput
              label={t('search_property')}
              placeholder={t('search_property_placeholder')}
              type={DropdownInputTypes.SEARCH}
              value={searchProperty}
              onClick={action}
              onBlur={() => searchProperty && getSearchProperty.refetch()}
              onChange={e => setSearchProperty(e.target.value)}
              onKeyDown={(e) => handleInputKeyDown(e, onClose)}
            />
          )}
        >

          {searchProperty && getSearchProperty.isSuccess && (!searchPropertyData || searchPropertyData.empty) &&
            <MUIDropdownItem>
              <Typography
                variant="text-md"
                color={GRAY_900}
                fontWeight="medium"
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {t('empty_property')}
              </Typography>
            </MUIDropdownItem>
          }

          {getSearchProperty.isSuccess && searchPropertyContent &&
            <Box>
              {searchPropertyContent.map((property, index) => (
                <MUIDropdownItem
                  key={property.id}
                  onClick={() => handleSelect(property)}
                  sx={{
                    ...(index === highlightedIndex && {
                      backgroundColor: BEIGE_600,
                    }),
                    '&:hover': {
                      backgroundColor: BEIGE_300,
                    },
                  }}
                >
                  <Stack direction="row" gap=".5rem" alignItems="center">

                    <Box borderRadius="0.8rem" >
                      <img alt="property-placeholder" src={propertyPlaceholder} style={{ width: '4.8rem', height: '4.8rem' }} />
                    </Box>

                    <Stack gap=".2rem">

                      <Typography variant="text-sm" color={GRAY_900} fontWeight="semiBold">
                        {property.title ?? property.address?.street}
                      </Typography>

                      {property.address &&
                        <Stack gap=".2rem" flexDirection="row">

                          <Typography variant="text-xs" color={GRAY_900} fontWeight="semiBold">
                            {property.address.street} {property.address.streetNumber},
                          </Typography>

                          <Typography variant="text-xs" color={GRAY_700}>
                            {property.address.city}, {property.address.postalCode}, {property.address.country}
                          </Typography>

                        </Stack>
                      }

                    </Stack>
                  </Stack>
                </MUIDropdownItem>
              ))}
            </Box>
          }

        </MUIDropdown>
      }

      {selectedProperty && (selectedProperty.address || selectedProperty.title) &&

        <Stack gap=".8rem" marginTop="1.2rem">

          <Typography variant="text-md" color={GRAY_900} fontWeight={600} lineHeight="2.4rem">{t('selected_property')}</Typography>

          <BorderBoxWrapper
            height={isUpdatePropertyAddressFlow ? 'auto' : '7.2rem'}
            alignContent="center"
            marginX="0.2rem"
            padding="1.2rem"
          >
            <Stack direction="row" gap=".5rem" alignItems="center">

              <Box borderRadius="0.8rem" >
                <img alt="property-placeholder" src={propertyPlaceholder} style={{ width: '4.8rem', height: '4.8rem' }} />
              </Box>

              <Stack width="100%" gap="1.2rem" flexDirection="row" justifyContent="space-between">

                <Stack gap=".2rem">

                  <Typography variant="text-sm" color={GRAY_900} fontWeight="semiBold">
                    {selectedProperty?.title ?? selectedProperty?.address?.street}
                  </Typography>

                  {selectedProperty.address &&
                    <Stack gap=".2rem" flexDirection="row">

                      <Typography variant="text-xs" color={GRAY_900} fontWeight="semiBold">
                        {selectedPropertyAddress.street} {selectedPropertyAddress.streetNumber},
                      </Typography>

                      <Typography variant="text-xs" color={GRAY_700}>
                        {selectedPropertyAddress.city}, {selectedPropertyAddress.postalCode}, {selectedPropertyAddress.country}
                      </Typography>

                    </Stack>
                  }

                  {!selectedProperty.address && isUpdatePropertyAddressFlow &&
                    <MUITooltip content={t('address_not_selected')}>
                      <Stack gap=".2rem" flexDirection="row">
                        <Typography variant="text-xs" color={GRAY_900}>
                          {t('missing_address')}
                        </Typography>
                      </Stack>
                    </MUITooltip>
                  }

                </Stack>
              </Stack>

              <IconButton
                size='medium'
                onClick={resetRealEstateProperty}
              >
                <DeleteOutlineRoundedIcon fontSize='large' sx={{ color: CORAL_500 }} />
              </IconButton>

            </Stack>
          </BorderBoxWrapper>

        </Stack>
      }
    </>
  )
}
